<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="image-selector-container">
    <div class="image-selector-wrapper" :class="{ collapsed: collapsed }">
      <div class="image-selector-toolbar">
        <button class="button is-small multi-window-button" :class="{ active: multiWindowMode }"
          :aria-label="multiWindowTooltip" @blur="hideToolbarTooltip" @click="toggleMultiWindowMode"
          @focus="showToolbarTooltip($event, multiWindowTooltip)" @mouseenter="showToolbarTooltip($event, multiWindowTooltip)"
          @mouseleave="hideToolbarTooltip">
          <span class="icon is-small">
            <i class="fas fa-th-large"></i>
          </span>
        </button>
        <button v-if="multiWindowMode" class="button is-small multi-window-button sync-window-button"
          :class="{ active: syncWindowMode }" :aria-label="syncWindowTooltip" @blur="hideToolbarTooltip"
          @click="toggleSyncWindowMode" @focus="showToolbarTooltip($event, syncWindowTooltip)"
          @mouseenter="showToolbarTooltip($event, syncWindowTooltip)" @mouseleave="hideToolbarTooltip">
          <span class="icon is-small">
            <i class="fas fa-link"></i>
          </span>
        </button>
        <span class="window-count">{{ viewerImageCount }}/{{ maxViewerImages }}</span>
      </div>
      <div>
        <div class="card" v-for="image in images" :key="image.id" :class="{ active: isImageActive(image) }">
          <a class="card-image" @click="addImage(image)">
            <img :src="appendShortTermToken(imageThumbUrl(image), shortTermToken)" :alt="$t('image-thumbnail')" />
            <div class="image-ai-status-badge" :class="{
              'is-processing': aiStatuses[image.id] === 'Process',
              'is-error': aiStatuses[image.id] === 'Error',
              'is-ready': aiStatuses[image.id] === 'Ready'
            }" v-if="aiStatuses[image.id]" :aria-label="getAiStatusHint(aiStatuses[image.id])">
              <span class="ai-status-label">AI</span>
              <span class="ai-status-icon icon is-small" v-if="aiStatuses[image.id] === 'Process'">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              <span class="ai-status-icon icon is-small" v-else-if="aiStatuses[image.id] === 'Error'">
                <i class="fas fa-exclamation-circle"></i>
              </span>
              <span class="ai-status-icon icon is-small" v-else-if="aiStatuses[image.id] === 'Ready'">
                <i class="fas fa-check-circle"></i>
              </span>
              <span class="ai-status-icon icon is-small" v-else>
                <i class="fas fa-robot"></i>
              </span>
            </div>
          </a>
          <div class="card-content" @click="addImage(image)">
            <div class="image-title-name">
              <image-name :image="image" class="image-name" />
            </div>
            <div class="label-container mt-1"
              v-if="project && !project.blindMode && !labelErrors[image.id] && labelDataUrls[image.id]">
              <img :src="labelDataUrls[image.id]" class="label-preview" :title="$t('image-label')"
                @error="$set(labelErrors, image.id, true)">
            </div>
          </div>
        </div>

        <!-- Pagination Component -->
        <div class="pagination-wrapper mt-3 mb-3 pr-2 pl-2" v-if="nbFilteredImages > perPage">
          <b-pagination :total="nbFilteredImages" :current.sync="currentPage" :per-page="perPage" order="is-centered"
            size="is-small" icon-prev="chevron-left" icon-next="chevron-right" />
        </div>
      </div>
    </div>
    <div class="image-selector-opener" @click="collapsed = !collapsed"
      :title="collapsed ? $t('expand') : $t('collapse')">
      <i class="fas" :class="collapsed ? 'fa-angle-right' : 'fa-angle-left'"></i>
    </div>
    <div v-if="toolbarTooltip.visible" class="toolbar-floating-tooltip"
      :style="{ top: toolbarTooltip.top + 'px', left: toolbarTooltip.left + 'px' }">
      {{ toolbarTooltip.label }}
    </div>
  </div>
</template>

<script>
import { get } from '@/utils/store-helpers';
import { IMAGE_FORMAT } from '@/utils/image-utils';

import ImageName from '@/components/image/ImageName';
import { ImageInstanceCollection } from '@/api';
import { appendShortTermToken } from '@/utils/token-utils.js';
import Cytomine from '@/api/cytomine';

const MAX_VIEWER_IMAGES = 4;
const PROCESSING_JOB_STATUSES = ['Processing', 'Running', 'Starting'];
const ERROR_JOB_STATUSES = ['Error', 'Failed'];

export default {
  name: 'image-selector',
  components: {
    ImageName
  },
  data() {
    return {
      images: [],
      currentPage: 1,
      perPage: 20,
      nbFilteredImages: 0,
      aiStatuses: {},
      labelDataUrls: {},
      labelErrors: {},
      multiWindowMode: false,
      syncWindowMode: false,
      maxViewerImages: MAX_VIEWER_IMAGES,
      switchingImageId: null,
      imageSwitchRequestId: 0,
      toolbarTooltip: {
        visible: false,
        label: '',
        top: 0,
        left: 0
      }
    };
  },
  computed: {
    project: get('currentProject/project'),
    shortTermToken: get('currentUser/shortTermToken'),
    activeImageGroupForViewer: get('project-tree/activeImageGroupForViewer'),
    viewerModule() {
      return this.$store.getters['currentProject/viewerModulePath'];
    },
    currentViewer() {
      return this.$store.getters['currentProject/currentViewer'] || {};
    },
    viewerImages() {
      return this.currentViewer.images || {};
    },
    collapsed: {
      get() {
        if (this.currentViewer.imageSelector !== undefined) {
          return !this.currentViewer.imageSelector;
        }
        return true;
      },
      set(value) {
        this.$store.commit(this.viewerModule + 'setImageSelector', !value);
      }
    },
    viewerImageIndexes() {
      return Object.keys(this.viewerImages);
    },
    viewerImageCount() {
      return Object.values(this.viewerImages)
        .filter(viewerImage => viewerImage?.imageInstance?.id).length;
    },
    multiWindowTooltip() {
      return this.multiWindowMode ? 'Multi-window enabled' : 'Open multiple windows';
    },
    syncWindowTooltip() {
      return this.syncWindowMode ? 'Zoom and pan sync enabled' : 'Sync zoom and pan';
    }
  },
  watch: {
    currentPage() {
      this.fetchImages();
    },
    viewerImageCount() {
      if (this.syncWindowMode) {
        this.syncViewerLinks();
      }
    }
  },
  methods: {
    appendShortTermToken,
    getAiStatusHint(status) {
      if (!status) return '';
      if (status === 'Process') return 'AI is currently analyzing this image.';
      if (status === 'Error') return 'An error occurred during AI analysis.';
      if (status === 'Ready') return 'AI analysis completed.';
      return `AI Status: ${status}`;
    },
    async fetchAllJobsStatus() {
      const jobsPromises = this.images.map(image =>
        Cytomine.instance.api.get(`ai-algorithm-jobs?imageId=${image.id}`)
          .then(response => {
            let jobs = response.data;
            if (response.data.collection) jobs = response.data.collection;
            else if (!Array.isArray(jobs)) jobs = [jobs];

            if (jobs && jobs.length > 0) {
              jobs.sort((a, b) => (a.created || 0) - (b.created || 0));
              const latestJob = jobs[jobs.length - 1];
              const status = latestJob.status;

              let finalStatus = 'Ready';
              if (PROCESSING_JOB_STATUSES.includes(status)) finalStatus = 'Process';
              else if (ERROR_JOB_STATUSES.includes(status)) finalStatus = 'Error';

              this.$set(this.aiStatuses, image.id, finalStatus);
            }
          })
          .catch(err => {
            console.error("Failed to fetch jobs for image ", image.id, err);
          })
      );
      await Promise.all(jobsPromises);
    },
    revokeLabelObjectUrls() {
      Object.values(this.labelDataUrls).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    },
    async fetchAllLabelImages() {
      if (this.project && this.project.blindMode) return;

      this.revokeLabelObjectUrls();
      this.labelDataUrls = {};
      this.labelErrors = {};

      const labelPromises = this.images.map(image => this.fetchLabelImage(image));
      await Promise.all(labelPromises);
    },
    async fetchLabelImage(image) {
      try {
        const url = `imageinstance/${image.id}/associated/label.jpg`;
        const { data } = await Cytomine.instance.api.get(url, { responseType: 'blob' });
        const objectUrl = URL.createObjectURL(data);
        this.$set(this.labelDataUrls, image.id, objectUrl);
      } catch (error) {
        this.$set(this.labelErrors, image.id, true);
      }
    },
    async getReferenceSlice(image) {
      await image.fetch();
      return image.fetchReferenceSlice();
    },
    beginImageSwitch(imageId) {
      this.imageSwitchRequestId += 1;
      this.switchingImageId = imageId;
      return this.imageSwitchRequestId;
    },
    isLatestImageSwitch(requestId) {
      return requestId === this.imageSwitchRequestId;
    },
    clearImageSwitch(requestId) {
      if (this.isLatestImageSwitch(requestId)) {
        this.switchingImageId = null;
      }
    },
    findViewerImageIndex(imageId) {
      return this.viewerImageIndexes.find(index =>
        this.viewerImages[index]?.imageInstance?.id === imageId
      );
    },
    activateViewerImage(imageId, requestId) {
      const imageIndex = this.findViewerImageIndex(imageId);
      if (imageIndex === undefined) return false;

      this.$store.commit(this.viewerModule + 'setActiveImage', imageIndex);
      this.clearImageSwitch(requestId);
      return true;
    },
    async addImageToNewWindow(image, requestId) {
      if (this.viewerImageCount >= this.maxViewerImages) {
        this.$notify({ type: 'warn', text: this.$t('notif-warn-max-open-images', { count: this.maxViewerImages }) });
        this.clearImageSwitch(requestId);
        return;
      }

      const slice = await this.getReferenceSlice(image);
      if (!this.isLatestImageSwitch(requestId)) return;

      await this.$store.dispatch(this.viewerModule + 'addImage', { image, slices: [slice] });
      this.$store.commit(this.viewerModule + 'setImageSelector', true);
      this.applyViewerLinks();
      this.clearImageSwitch(requestId);
    },
    async replaceActiveImage(image, requestId) {
      const slice = await this.getReferenceSlice(image);
      if (!this.isLatestImageSwitch(requestId)) return;

      await this.$store.dispatch(
        `${this.viewerModule}images/${this.currentViewer.activeImage}/setImageInstance`,
        { image, slices: [slice] }
      );
      this.clearImageSwitch(requestId);
    },
    async addImage(image) {
      const requestId = this.beginImageSwitch(image.id);

      try {
        if (this.activateViewerImage(image.id, requestId)) return;

        if (this.multiWindowMode) {
          await this.addImageToNewWindow(image, requestId);
          return;
        }

        await this.replaceActiveImage(image, requestId);
      } catch (error) {
        this.clearImageSwitch(requestId);
        console.error('Failed to add image to viewer', error);
        // this.$notify({type: 'error', text: this.$t('notif-error-add-viewer-image')});
      }
    },
    createImageCollection() {
      const baseParams = {
        nbPerPage: this.perPage,
        sort: 'id',
        order: 'asc'
      };

      if (this.activeImageGroupForViewer) {
        return new ImageInstanceCollection({
          ...baseParams,
          filterKey: 'imagegroup',
          filterValue: this.activeImageGroupForViewer,
        });
      }

      return new ImageInstanceCollection({
        ...baseParams,
        filterKey: 'project',
        filterValue: this.project.id,
      });
    },
    async fetchImages() {
      try {
        const collection = this.createImageCollection();
        const data = (await collection.fetchPage(this.currentPage - 1));
        this.images = data.array;
        this.nbFilteredImages = data.totalNbItems;

        this.fetchAllJobsStatus();
        this.fetchAllLabelImages();
      } catch (error) {
        console.error('Failed to fetch images', error);
      }
    },

    toggle() {
      this.collapsed = !this.collapsed;
    },

    toggleMultiWindowMode() {
      this.multiWindowMode = !this.multiWindowMode;

      if (this.multiWindowMode) {
        this.syncWindowMode = false;
        this.clearViewerLinks();
        if (this.collapsed) {
          this.collapsed = false;
        }
        return;
      }

      this.syncWindowMode = false;
      this.clearViewerLinks();
    },

    toggleSyncWindowMode() {
      this.syncWindowMode = !this.syncWindowMode;
      this.applyViewerLinks();
    },

    applyViewerLinks() {
      if (this.syncWindowMode) {
        this.syncViewerLinks();
        return;
      }

      this.clearViewerLinks();
    },

    syncViewerLinks() {
      this.$store.commit(this.viewerModule + 'setLinkMode', 'ABSOLUTE');
      this.$store.commit(this.viewerModule + 'setLinks', this.viewerImageIndexes.length > 1 ? [this.viewerImageIndexes] : []);
    },

    clearViewerLinks() {
      this.$store.commit(this.viewerModule + 'setLinks', []);
    },

    showToolbarTooltip(event, label) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.toolbarTooltip = {
        visible: true,
        label,
        top: Math.round(rect.top + rect.height / 2),
        left: Math.round(rect.right + 8)
      };
    },

    hideToolbarTooltip() {
      this.toolbarTooltip.visible = false;
    },

    shortkeyHandler(key) {
      if (key === 'toggle-add-image') {
        this.toggle();
      }
    },

    isImageActive(image) {
      return this.findViewerImageIndex(image.id) !== undefined || this.switchingImageId === image.id;
    },
    imageThumbUrl(image) {
      return image.thumbURL(256, IMAGE_FORMAT);
    }
  },
  created() {
    this.fetchImages();
  },
  mounted() {
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
  },
  beforeDestroy() {
    this.$store.dispatch('project-tree/setActiveImageGroupForViewer', null);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
    this.hideToolbarTooltip();
    this.revokeLabelObjectUrls();
  }
};
</script>

<style scoped lang="scss">
@import '../../assets/styles/dark-variables';

/* 定义滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: $dark-wapper-bg !important;
}

::-webkit-scrollbar-thumb {
  background: $dark-scrollbar-thumb !important;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: $dark-scrollbar-thumb-hover !important;
}

.image-selector-container {
  display: flex;
  height: 100%;
}

.image-selector-wrapper {
  @include clinical-workspace-rail;
  display: flex;
  flex-direction: column;
  width: 8em;
  height: 100%;
  z-index: 150;
  overflow-y: auto;
  overflow-x: hidden;
  border-right-width: 1px;
  border-right-style: solid;
  transition: width 0.3s ease, min-width 0.3s ease, padding 0.3s ease;
}

.image-selector-wrapper.collapsed {
  width: 0;
  min-width: 0;
  border-right: none;
  overflow: hidden;
}

.image-selector-toolbar {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.25rem;
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgba(10, 21, 36, 0.96);
  border-bottom: 1px solid rgba(190, 214, 238, 0.1);
}

.multi-window-button {
  @include clinical-icon-control(2rem, 5px);
  flex: 0 0 auto;
  background: rgba(222, 238, 255, 0.055);
  border-color: $dark-border-color;
  color: $dark-text-primary;

  &.active,
  &:active,
  &:focus,
  &:hover {
    background: linear-gradient(180deg, $primary, #42c7da);
    border-color: $primary;
    color: #06121d;
    box-shadow: 0 0 0 0.125em rgba($primary, 0.25);
    outline: none;
  }

  &:not(.active):focus {
    background: $dark-bg-tertiary;
    border-color: $primary;
    color: $dark-text-primary;
  }

  .icon,
  .fas {
    color: inherit;
  }
}

.toolbar-floating-tooltip {
  position: fixed;
  z-index: 10000;
  transform: translateY(-50%);
  max-width: 14rem;
  padding: 0.35rem 0.55rem;
  border-radius: 6px;
  background: rgba(13, 25, 42, 0.98);
  border: 1px solid $dark-border-color;
  color: $dark-text-primary;
  font-size: 0.75rem;
  line-height: 1.25;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
}

.window-count {
  color: $dark-text-secondary;
  font-size: 0.72rem;
  line-height: 1;
}

.image-selector-opener {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.2rem;
  background: linear-gradient(180deg, #101c2e, #091421);
  border-right: 1px solid rgba($primary, 0.12);
  cursor: pointer;
  color: $dark-text-secondary;
  z-index: 151;
}

.image-selector-opener:hover {
  background-color: rgba($primary, 0.12);
  color: $primary;
}

.card {
  margin: 0.35em 0.28em;
  background: rgba(222, 238, 255, 0.052);
  border: 1px solid rgba(222, 238, 255, 0.1);
  border-radius: 7px;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, transform 0.15s ease;
}

.card:hover {
  background: rgba(222, 238, 255, 0.075);
  border-color: rgba($primary, 0.28);
}

.card.active {
  background: linear-gradient(180deg, rgba($primary, 0.18), rgba($primary, 0.11));
  border-color: rgba($primary, 0.9);
  box-shadow: 0 0 0 1px rgba($primary, 0.68), 0 14px 30px rgba($primary, 0.16);
}

.card.active .card-image {
  box-shadow: inset 0 0 0 2px $primary;
}

.card.active .card-content {
  color: $dark-text-primary;
}

.card-image {
  display: block;
  position: relative;
  width: 100%;
  background-color: transparent;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  /* Ensure the image corners are rounded */
}

.card-image img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  background: #f7fafc;
}

.card-content {
  padding: 0.55em;
  font-size: 0.82rem;
  overflow: hidden;
  text-align: center;
}

.image-title-name {
  min-width: 0;
  text-align: left;
}

.image-name {
  min-width: 0;
  overflow: visible;
  overflow-wrap: anywhere;
  text-align: left;
  color: $dark-text-secondary;
  line-height: 1.35;
  white-space: normal;
  word-break: break-word;
}

.image-ai-status-badge {
  align-items: center;
  backdrop-filter: blur(4px);
  background: rgba(9, 20, 34, 0.92);
  border: 1px solid rgba(190, 214, 238, 0.28);
  border-radius: 6px;
  bottom: 0.32rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.28);
  color: $dark-text-primary;
  display: flex;
  gap: 0.18rem;
  height: 1.45rem;
  justify-content: center;
  padding: 0 0.32rem;
  position: absolute;
  right: 0.32rem;
  z-index: 1;
}

.image-ai-status-badge.is-processing {
  border-color: rgba(255, 221, 87, 0.55);
  color: #ffdd57;
}

.image-ai-status-badge.is-error {
  border-color: rgba(255, 88, 105, 0.58);
  color: #ff6b7d;
}

.image-ai-status-badge.is-ready {
  border-color: rgba(72, 199, 116, 0.55);
  color: #48c774;
}

.ai-status-label {
  color: inherit;
  font-size: 0.58rem;
  font-weight: 700;
  line-height: 1;
}

.ai-status-icon {
  align-items: center;
  color: inherit;
  display: flex;
  height: 0.9rem;
  justify-content: center;
  line-height: 1.35;
  width: 0.9rem;
}

.ai-status-icon .fas {
  color: inherit;
  font-size: 0.72rem;
}

.pagination-wrapper {
  border-top: 1px solid $dark-border-color;
  padding-top: 0.75rem;
}

.label-container {
  width: 100%;
  padding-top: 0.2rem;
}

.label-preview {
  object-fit: contain;
  width: 100%;
  max-height: 128px;
  height: auto;
  display: block;
  margin: 0 auto;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
}
</style>
